import {
  AccessOptionForTaskDto,
  AssetLinkDto,
  ArchivedTaskDto,
  CreateAssetLinkDto,
  CreateTaskAccessDto,
  CreateTaskWithAccessDto,
  GetTaskDto,
  RecordedTimeDto,
  ReplaceTaskAccessDto,
  StartNoteTimerDto,
  UpdateAssetLinkDto,
  UpdateTaskDto,
  GetArchivedNotes,
  RestoredTaskDto,
  GuardedUpdateRecordedTimeDto,
  UpdateRecordedTimeDto,
  GetActiveRecordingDto,
  CreateManyTasksWithAccessDto,
  DeleteTaskResponseDto,
  RecordingInRangeDto,
  ReflectWeekDto,
} from "./types";
import { ScheduleItemDto } from "../calendar/calendar.types";
import { taskSocketManager } from "../../services/appRegistry";
import { socketRequest } from "../../services/socketRequest";

const request = <T>(
  event: string,
  payload?: unknown,
  options?: Parameters<typeof socketRequest<T>>[3],
) => socketRequest<T>(taskSocketManager, event, payload, options);

const togglePauseTimer = (): Promise<RecordedTimeDto> =>
  request("togglePauseTimer");

const startNoteRecord = (
  recordData: StartNoteTimerDto,
): Promise<RecordedTimeDto> =>
  request("startNoteTimer", {
    id: recordData.noteId,
    data: {
      noteId: recordData.noteId,
    },
  });

const endNoteRecord = (noteId: string): Promise<RecordedTimeDto> =>
  request("endNoteTimer", { id: noteId });

const updateNoteRecord = (
  updatedData: UpdateRecordedTimeDto,
): Promise<RecordedTimeDto> => {
  if (!updatedData.noteId) return Promise.reject(Error());

  const message: GuardedUpdateRecordedTimeDto = {
    id: updatedData.noteId,
    data: updatedData,
  };
  return request("updateNoteTimerRecord", message);
};

const deleteNoteRecord = ({
  recordId,
  noteId,
}: {
  recordId: string;
  noteId: string;
}): Promise<string> =>
  request("deleteNoteTimerRecord", { id: noteId, recordId: recordId });

const getNoteRecords = (id: string): Promise<RecordedTimeDto[]> =>
  request("getNoteTimerRecords", { id });

interface GetTasksDto extends GetTaskDto {
  NoteScheduling: ScheduleItemDto[];
}
//get user tasks
const getTasks = (): Promise<GetTaskDto[]> =>
  request("getTasks", undefined, {
    // The schedule comes as NoteScheduling
    transform: (response: GetTasksDto[]) =>
      response.map(({ NoteScheduling, ...rest }) => ({
        schedule: NoteScheduling,
        ...rest,
      })),
  });

//get user tasks
const getCompletedTasks = (
  startIndex?: number,
): Promise<GetArchivedNotes[]> => request("getCompletedTasks", { startIndex });

//get user tasks
const getArchivedTask = (id?: string): Promise<ArchivedTaskDto> =>
  request("getArchivedTask", { id });

//get user tasks
const restoreArchivedTask = (id?: string): Promise<RestoredTaskDto> =>
  request("restoreArchivedTask", { id });

//getDeletedTasks
const getDeletedTasks = (startIndex?: number): Promise<GetArchivedNotes[]> =>
  request("getDeletedTasks", { startIndex });

const createTaskAccess = (
  createTaskAccessData: CreateTaskAccessDto,
): Promise<AccessOptionForTaskDto> =>
  request("createTaskAccess", createTaskAccessData);

const replaceTaskAccess = (
  replaceTaskAccessData: ReplaceTaskAccessDto,
): Promise<AccessOptionForTaskDto> =>
  request("replaceTaskAccess", replaceTaskAccessData);

const deleteTaskAccess = (
  accessId: string,
  noteId: string,
): Promise<string> =>
  request("deleteTaskAccess", { id: noteId, accessId: accessId });

const deleteTask = (taskId: string): Promise<DeleteTaskResponseDto> =>
  request("deleteTask", { id: taskId });

const finishTask = (taskId: string): Promise<DeleteTaskResponseDto> =>
  request("finishTask", { id: taskId });

const getTask = (taskId: string): Promise<GetTaskDto> => {
  return new Promise((resolve, reject) => {
    const message = { id: taskId };
    // The guard sends taskError for any missing task, only react to this one
    const onTaskError = (error: { id?: string }) => {
      if (error?.id && error.id !== taskId) return;
      taskSocketManager?.off("taskError", onTaskError);
      reject(error);
    };
    taskSocketManager?.emit("getTask", message);
    taskSocketManager?.once("getTaskAnswer", (response) => {
      taskSocketManager?.off("taskError", onTaskError);
      try {
        const parsedResponse = response;
        resolve(parsedResponse);
      } catch (error) {
        reject(error);
      }
    });
    // Handle error event if the server sends any
    taskSocketManager?.on("taskError", onTaskError);
  });
};

const getActiveRecording = (): Promise<GetActiveRecordingDto> =>
  request("getActiveRecording", undefined, { errorEvent: "taskError" });

const createAssetLink = (
  assetData: CreateAssetLinkDto,
): Promise<AssetLinkDto> =>
  request("createAssetLink", {
    id: assetData.noteId,
    createAssetData: {
      noteId: assetData.noteId,
      assetType: assetData.assetType,
      assetValue: assetData.assetValue,
      unit: assetData.unit,
    },
  });

const updateAssetLinkData = (
  assetData: UpdateAssetLinkDto,
): Promise<AssetLinkDto> =>
  request("updateAssetLink", {
    id: assetData.noteId,
    updateAssetData: {
      id: assetData.id,
      noteId: assetData.noteId,
      assetValue: assetData.assetValue,
    },
  });

const updateTask = (updateData: UpdateTaskDto): Promise<GetTaskDto> =>
  request("updateTask", { id: updateData.id, data: updateData });

const createQuickTask = (): Promise<GetTaskDto> => request("createQuickTask");

const createTask = (
  createTaskData: CreateTaskWithAccessDto,
): Promise<GetTaskDto> => request("createTask", createTaskData);

const createTasks = (
  createTaskData: CreateManyTasksWithAccessDto,
): Promise<GetTaskDto[]> => request("createTasks", createTaskData);

const ACK_TIMEOUT_MS = 10_000;

/**
 * Emits and waits for the socket acknowledgement: the answer arrives in the
 * callback of exactly this request (no `once` listener that could catch
 * another answer). The timeout covers requests the gateway rejects before
 * answering (e.g. a payload failing validation) - then the callback is never
 * called.
 */
const emitWithAck = <T>(
  event: string,
  payload: unknown,
  fallbackError: string,
): Promise<T> => {
  return new Promise((resolve, reject) => {
    if (!taskSocketManager) {
      reject(new Error("Socket not available"));
      return;
    }
    let settled = false;
    const timeout = setTimeout(() => {
      if (settled) return;
      settled = true;
      reject(new Error(`Timed out: ${event}`));
    }, ACK_TIMEOUT_MS);

    taskSocketManager.emit(
      event,
      payload,
      (answer: { data: T } | { error: string }) => {
        if (settled) return;
        settled = true;
        clearTimeout(timeout);
        if (answer && "data" in answer) resolve(answer.data);
        else reject(new Error(answer?.error || fallbackError));
      },
    );
  });
};

/** The user's recordings (work + breaks) overlapping [from, to), max 31 days */
const getRecordingsInRange = (
  from: string,
  to: string,
): Promise<RecordingInRangeDto[]> =>
  emitWithAck("getRecordingsInRange", { from, to }, "Could not load recordings");

/** Rückblick data for [from, to) - one week */
const getReflectWeek = (from: string, to: string): Promise<ReflectWeekDto> =>
  emitWithAck("getReflectWeek", { from, to }, "Could not load the review");

const taskService = {
  getRecordingsInRange,
  getReflectWeek,
  createTask,
  createTasks,
  createQuickTask,
  createAssetLink,
  startNoteRecord,
  updateNoteRecord,
  createTaskAccess,
  getTask,
  getCompletedTasks,
  getDeletedTasks,
  getArchivedTask,
  getActiveRecording,
  getTasks,
  restoreArchivedTask,
  getNoteRecords,
  updateTask,
  updateAssetLinkData,
  togglePauseTimer,
  replaceTaskAccess,
  deleteTaskAccess,
  finishTask,
  deleteTask,
  endNoteRecord,
  deleteNoteRecord,
};
export default taskService;
